import { z } from 'zod';
import { integrationConfigValuesSchema, integrationDisplayNameSchema, type MessagingProviderSummary } from './integration-config.schemas';

/**
 * WhatsApp providers a deployment can install (Infobip). One sends at a time, as with SMS. Nothing sends by WhatsApp
 * yet: notifications built later go out by WhatsApp when a provider sends, and by SMS when it doesn't or refuses.
 */

export type WhatsappProviderSummary = MessagingProviderSummary;

/** Installing checks the credentials with the provider without sending a message; a refusal saves nothing. */
export const installWhatsappIntegrationSchema = z
  .object({
    providerKey: z.string().min(1),
    displayName: integrationDisplayNameSchema,
    config: integrationConfigValuesSchema,
  })
  .strict();
export type InstallWhatsappIntegrationInput = z.infer<typeof installWhatsappIntegrationSchema>;

/** A secret left out keeps its stored value; one supplied replaces it. Changed credentials are checked like an install. */
export const updateWhatsappIntegrationSchema = z
  .object({
    displayName: integrationDisplayNameSchema.optional(),
    config: integrationConfigValuesSchema.optional(),
  })
  .strict();
export type UpdateWhatsappIntegrationInput = z.infer<typeof updateWhatsappIntegrationSchema>;

export interface WhatsappIntegration {
  id: string;
  providerKey: string;
  providerName: string;
  displayName: string;
  /** The one installation WhatsApp messages go out through; at most one is. */
  isSending: boolean;
  /** Non-secret values only. */
  config: Record<string, string | number | boolean>;
  /** Names of the secret fields that hold a value: the form shows "Saved", never the value. */
  secretsSet: string[];
  /** When the provider last accepted these credentials. */
  lastCheckedAt: string | null;
  createdAt: string;
}
